import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Home.css";
import SEO from "../components/SEO";
import MagneticButton from "../components/MagneticButton";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Interactive wave logo (restored)
const InteractiveLogo = () => {
  const containerRef = useRef(null);
  const requestRef = useRef();
  const isRipplingRef = useRef(false);
  const timeRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const waveGroups = container.querySelectorAll('g[data-letter]');

    const idleWaveAnimation = () => {
      if (!isRipplingRef.current) {
        timeRef.current += 0.02;

        waveGroups.forEach((group, index) => {
          const textEl = group.querySelector('text');
          const yOffset = Math.sin(timeRef.current + index * 0.5) * 6;
          const rotation = Math.sin(timeRef.current * 0.6 + index * 0.3) * 1.5;

          try {
            const bbox = textEl.getBBox();
            const centerX = bbox.x + bbox.width / 2;
            const centerY = bbox.y + bbox.height / 2;

            textEl.setAttribute(
              "transform",
              `translate(0, ${yOffset}) rotate(${rotation}, ${centerX}, ${centerY})`
            );
          } catch (e) {
            // ignore occasional getBBox issues
          }
        });
      }
      requestRef.current = requestAnimationFrame(idleWaveAnimation);
    };

    const animateLetterWave = (group, distance, isEpicenter = false) => {
      const textEl = group.querySelector('text');
      if (!textEl) return;

      const maxHeight = isEpicenter ? -30 : -25;
      const heightDecay = distance * 2.5;
      const waveHeight = Math.max(maxHeight + heightDecay, -8);

      const maxScale = isEpicenter ? 1.2 : 1.15;
      const scaleDecay = distance * 0.02;
      const waveScale = Math.max(maxScale - scaleDecay, 1.05);

      const bbox = textEl.getBBox();
      const centerX = bbox.x + bbox.width / 2;
      const centerY = bbox.y + bbox.height / 2;

      const currentTransform = textEl.getAttribute("transform") || "translate(0, 0)";

      const upAnimation = textEl.animate(
        [
          { transform: currentTransform, filter: "brightness(1)" },
          {
            transform: `translate(0, ${waveHeight}) scale(${waveScale})`,
            transformOrigin: `${centerX}px ${centerY}px`,
            filter:
              "brightness(1.3) drop-shadow(0 0 10px rgba(45, 156, 219, 0.8))",
          },
        ],
        {
          duration: 300,
          easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          fill: "forwards",
        }
      );

      upAnimation.onfinish = () => {
        const downAnimation = textEl.animate(
          [
            {
              transform: `translate(0, ${waveHeight}) scale(${waveScale})`,
              transformOrigin: `${centerX}px ${centerY}px`,
              filter: "brightness(1.3)",
            },
            {
              transform: "translate(0, 0) scale(1)",
              transformOrigin: `${centerX}px ${centerY}px`,
              filter: "brightness(1)",
            },
          ],
          {
            duration: 400,
            easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            fill: "forwards",
          }
        );

        downAnimation.onfinish = () => {
          textEl.removeAttribute("style");
        };
      };
    };

    const triggerRippleWave = (startIndex) => {
      const totalChars = waveGroups.length;
      const maxDistance = Math.max(startIndex, totalChars - 1 - startIndex);

      animateLetterWave(waveGroups[startIndex], 0, true);

      for (let distance = 1; distance <= maxDistance; distance++) {
        setTimeout(() => {
          if (startIndex - distance >= 0)
            animateLetterWave(waveGroups[startIndex - distance], distance);
          if (startIndex + distance < totalChars)
            animateLetterWave(waveGroups[startIndex + distance], distance);
        }, distance * 80);
      }

      const totalDuration = maxDistance * 80 + 700;
      setTimeout(() => {
        isRipplingRef.current = false;
        waveGroups.forEach((group) => {
          const textEl = group.querySelector("text");
          const animations = textEl.getAnimations();
          animations.forEach((anim) => anim.cancel());
          textEl.removeAttribute("style");
        });
      }, totalDuration);
    };

    const handleCharClick = (e, index) => {
      e.stopPropagation();
      if (isRipplingRef.current) return;
      isRipplingRef.current = true;
      triggerRippleWave(index);
    };

    waveGroups.forEach((group, index) => {
      group.style.cursor = "pointer";
      group.style.pointerEvents = "all";
      const textEl = group.querySelector("text");

      group.addEventListener("mouseenter", () => {
        if (!isRipplingRef.current) {
          textEl.style.transition = "filter 0.2s ease";
          textEl.style.filter =
            "brightness(1.4) drop-shadow(0 0 20px rgba(45, 156, 219, 1))";
        }
      });

      group.addEventListener("mouseleave", () => {
        if (!isRipplingRef.current) {
          textEl.style.filter = "";
        }
      });

      const listener = (e) => handleCharClick(e, index);
      group.addEventListener("click", listener);
      group._waveListener = listener;
    });

    requestRef.current = requestAnimationFrame(idleWaveAnimation);

    return () => {
      cancelAnimationFrame(requestRef.current);
      waveGroups.forEach((group) => {
        if (group._waveListener)
          group.removeEventListener("click", group._waveListener);
      });
    };
  }, []);

  return (
    <div className="wavetech-logo-container" ref={containerRef}>
      <svg
        id="wavetech-logo"
        viewBox="0 0 600 150"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2D9CDB" />
            <stop offset="50%" stopColor="#56CCF2" />
            <stop offset="100%" stopColor="#2F80ED" />
          </linearGradient>
          <filter id="wave-glow">
            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g data-letter="0">
          <text
            x="30"
            y="90"
            fontSize="85"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="800"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            W
          </text>
        </g>
        <g data-letter="1">
          <text
            x="115"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            a
          </text>
        </g>
        <g data-letter="2">
          <text
            x="175"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            v
          </text>
        </g>
        <g data-letter="3">
          <text
            x="235"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            e
          </text>
        </g>
        <g data-letter="4">
          <text
            x="310"
            y="90"
            fontSize="85"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="800"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            T
          </text>
        </g>
        <g data-letter="5">
          <text
            x="385"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            e
          </text>
        </g>
        <g data-letter="6">
          <text
            x="445"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            c
          </text>
        </g>
        <g data-letter="7">
          <text
            x="505"
            y="90"
            fontSize="75"
            fontFamily="'Inter', 'Arial', sans-serif"
            fontWeight="700"
            fill="url(#wave-gradient)"
            filter="url(#wave-glow)"
          >
            h
          </text>
        </g>
      </svg>
    </div>
  );
};

function Home() {
  return (
    <>
      <SEO />
      <main className="homepage">
        {/* SECTION 1 - HERO */}
        <section className="hero-section">
          <div className="hero-container">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1 className="hero-title">
                We Engineer the Systems Your Business Runs On.
              </h1>
              <p className="hero-subtitle">
                Custom web apps, Shopify infrastructure, automations, dashboards and integrations.
              </p>
              <div className="hero-ctas">
                <MagneticButton href="/contact" className="cta-primary hero-cta">
                  Work With Us
                </MagneticButton>
                <MagneticButton href="/services" className="cta-secondary hero-cta-secondary">
                  See What We Build
                </MagneticButton>
              </div>
            </motion.div>
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <div className="wavetech-logo-container">
                <InteractiveLogo />
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 - SELECTIVITY BAND */}
        <section className="selectivity-section">
          <div className="container">
            <div className="selectivity-card">
              <p className="selectivity-eyebrow">Selective Partnerships</p>
              <h2>We work with a small number of clients on purpose.</h2>
              <p className="selectivity-copy">
                We don&apos;t run a ticket queue. We embed, learn the business, architect the
                system, and build it clean. That level of depth requires focus, so we cap active
                engagements.
              </p>
              <p className="selectivity-meta">Currently accepting 2–3 new engagements.</p>
            </div>
          </div>
        </section>

        {/* SECTION 3 - WHAT WE BUILD */}
        <section className="what-we-build-section">
          <div className="container">
            <h2 className="section-heading">What We Build</h2>
            <div className="build-grid">
              <div className="build-card">
                <h3>Shopify Storefronts & Theme Systems</h3>
                <p>
                  Opinionated, fast storefronts and theme architectures tailored to how your catalog
                  and merchandising actually work.
                </p>
              </div>
              <div className="build-card">
                <h3>Custom Web Apps & Dashboards</h3>
                <p>
                  Internal tools and dashboards that give operators real visibility into inventory,
                  fulfillment, pricing, and performance.
                </p>
              </div>
              <div className="build-card">
                <h3>Automations & Integrations</h3>
                <p>
                  Glue between the systems you already use: Shopify, ERPs, CRMs, and analytics, with
                  clear, observable data flows.
                </p>
              </div>
              <div className="build-card">
                <h3>SaaS & Shopify Apps</h3>
                <p>
                  Net-new products like Wave Notify and Wave Sync, from concept to shipped,
                  approved, and maintained.
                </p>
              </div>
              <div className="build-card">
                <h3>Pricing & Analytics Systems</h3>
                <p>
                  Data models, reporting, and experimentation infrastructure so decisions aren&apos;t
                  made off spreadsheets alone.
                </p>
              </div>
              <div className="build-card">
                <h3>Long-Term Infrastructure Partner</h3>
                <p>
                  Ongoing ownership of the systems we build, with a small, focused roadmap rather than
                  endless one-off tickets.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 - OUR APPS */}
        <section className="apps-section">
          <div className="container">
            <h2 className="section-heading">Our Apps</h2>
            <div className="apps-grid">
              <motion.div
                className="app-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                whileHover={{ y: -4 }}
              >
                <h3>
                  <Link to="/wavenotify">Wave Notify</Link>
                </h3>
                <p className="app-category">Restock Alerts & Sales Recovery</p>
                <p>
                  Back-in-stock infrastructure for Shopify that captures demand when inventory runs out
                  and converts it when products return.
                </p>
                <ul className="app-features">
                  <li>Automatic &quot;Notify Me&quot; capture on sold-out products</li>
                  <li>Email notifications when inventory returns</li>
                  <li>Customer tagging and segmentation in Shopify</li>
                </ul>
                <Link to="/wavenotify" className="app-link">
                  Learn more about Wave Notify →
                </Link>
              </motion.div>

              <motion.div
                className="app-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                whileHover={{ y: -4 }}
              >
                <h3>
                  <Link to="/wavesync">Wave Sync</Link>
                </h3>
                <p className="app-category">Product Syncing & Inventory Management</p>
                <p>
                  Product- and inventory-sync infrastructure for complex catalogs, bundles, and
                  mirrored listings inside Shopify.
                </p>
                <ul className="app-features">
                  <li>One-way or bidirectional inventory syncing</li>
                  <li>Price and status alignment between linked products</li>
                  <li>Sync history and visibility for operators</li>
                </ul>
                <Link to="/wavesync" className="app-link">
                  Learn more about Wave Sync →
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 5 - THE PROCESS */}
        <section className="process-section">
          <div className="container">
            <h2 className="section-heading">How We Work</h2>
            <div className="process-steps">
              <div className="process-step">
                <div className="step-number">1</div>
                <h3>Discover</h3>
                <p>We learn the business and the real constraint, not just the feature request.</p>
              </div>
              <div className="process-step">
                <div className="step-number">2</div>
                <h3>Architect</h3>
                <p>We design infrastructure, not just pages: data, integrations, and failure modes.</p>
              </div>
              <div className="process-step">
                <div className="step-number">3</div>
                <h3>Build</h3>
                <p>We implement clean, scalable systems with an eye on the next 12–24 months.</p>
              </div>
              <div className="process-step">
                <div className="step-number">4</div>
                <h3>Maintain</h3>
                <p>We stay involved as a long-term partner, not a one-off project vendor.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 - PROOF / TRUST */}
        <section className="trust-section">
          <div className="container">
            <div className="trust-layout">
              <div className="trust-copy">
                <h2>Built in public. Real systems. Real outcomes.</h2>
                <p>
                  WaveTech is a verified Shopify Partner and the team behind production apps like
                  Wave Notify and Wave Sync. We focus on infrastructure you can see, ship, and own.
                </p>
                <ul className="trust-list">
                  <li>Capabilities across Shopify apps, custom web apps, and internal tools.</li>
                  <li>Deep focus on data flows, operations, and long-term maintainability.</li>
                  <li>Recent work and deeper case studies available on request.</li>
                </ul>
              </div>
              <div className="trust-badge-block">
                <img
                  src="/shopify_logo_white.png"
                  alt="Shopify Partner"
                  className="trust-shopify-badge"
                />
                <p className="trust-badge-caption">Verified Shopify Solutions Partner</p>
              </div>
            </div>
          </div>
        </section>

      </main>
    </>
  );
}

export default Home;
